import {useEffect, useState} from "react";
import {isEmpty} from "lodash";
import {I18nString} from "../../models/lang/i18nString";
import {
    fixedAmount,
    IMenuBasicOrder,
    percentageCharge,
    range,
    rangeCharges
} from "../../models/settings/menu";
import {useGlobalContext, useUserContext} from "../globalContext";
import {ChargeItem} from "../../components/form/chargesEditor";
import {Notify} from "../../migration/shared/lib/notification/notification";

function parseChargeFromApi(charge: fixedAmount | percentageCharge | rangeCharges): ChargeItem {
    switch (charge.type) {
        case "fixedAmount":
            return {type: 0, value: charge.data.value, label: charge.data.name};
        case "percentageCharge":
            return {type: 1, value: charge.data.percents, label: charge.data.name};
        case "rangeCharge":
            return {
                type: 2,
                value: (charge.data.ranges || []).map((r) => ({
                    from: r.from / 100,
                    to: r.to / 100,
                    value: r.value / 100,
                })),
                label: charge.data.name,
            };
    }
}

function parseChargesForApi(charges: ChargeItem[]): (fixedAmount | percentageCharge | rangeCharges)[] {
    return charges.map((item) => {
        switch (item.type) {
            case 0:
                return {
                    type: "fixedAmount",
                    data: {name: item.label, value: item.value as number},
                } as fixedAmount;
            case 1:
                return {
                    type: "percentageCharge",
                    data: {name: item.label, percents: item.value as number},
                } as percentageCharge;
            case 2:
                return {
                    type: "rangeCharge",
                    data: {
                        name: item.label,
                        ranges: (item.value as range[]).map((r) => ({
                            from: r.from * 100,
                            to: r.to * 100,
                            value: r.value * 100,
                        })),
                    },
                } as rangeCharges;
        }
        return {} as any;
    });
}

export function useSettingBasicOrder() {
    const {i18n, api} = useGlobalContext();
    const {currentMenuId} = useUserContext();
    const [charges, setCharges] = useState<ChargeItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [saveLoading, setSaveLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!currentMenuId) return;
        const controller = new AbortController();
        api.getMenuBasicOrder({setLoading, controller}, currentMenuId).then((res) => {
            if (res.success) {
                const data = (res.data as IMenuBasicOrder) || {charges: []};
                setCharges((data.charges || []).map(parseChargeFromApi));
            }
        });
        return () => controller.abort();
    }, [currentMenuId]);

    function validate(): boolean {
        const names = new Map<string, number>();
        for (const c of charges) {
            if (!c.label || isEmpty(c.label)) return false;
            const keys = Object.keys(c.label);
            for (const k of keys) {
                if (!c.label[k]) return false;
            }
            const fingerprint = JSON.stringify(c.label);
            names.set(fingerprint, (names.get(fingerprint) || 0) + 1);
            if (c.type === 2) {
                const ranges = c.value as range[];
                if (!ranges?.length) return false;
                for (const r of ranges) {
                    if (r.from < 0 || r.to < 0) return false;
                    if (r.to < r.from) return false;
                }
            }
        }
        let dup = false;
        names.forEach((count) => {
            if (count > 1) dup = true;
        });
        return !dup;
    }

    async function handleSave() {
        if (!currentMenuId) return;
        if (!validate()) {
            Notify.Warning({title: i18n.title() + ": " + i18n.save(), message: ""});
            return;
        }
        await api.updateMenuBasicOrder({setLoading: setSaveLoading}, {
            menuId: currentMenuId,
            charges: parseChargesForApi(charges),
        });
    }

    return {
        charges,
        setCharges,
        loading,
        saveLoading,
        handleSave,
    };
}
